import { useState } from 'react';
import amperaLogo from '../../assests/ampera_logo.png';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isValidIndonesianPhone } from '../../lib/utils';
import LanguageSwitcher from '../../components/layout/LanguageSwitcher';
import { Button, Field } from '../../components/ui';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, sendOtp, verifyOtp, loading: authLoading } = useAuth();

  // Form state
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('credentials'); // 'credentials' or 'otp'

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Get redirect destination
  const from = location.state?.from?.pathname || '/';

  // Handle phone/password login
  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!isValidIndonesianPhone(phone)) {
      setError(t('auth.invalidPhone'));
      setLoading(false);
      return;
    }

    const { data, error: signInError } = await signIn(phone, password);

    if (signInError) {
      setError(signInError.message || t('auth.loginFailed'));
      setLoading(false);
      return;
    }

    setLoading(false);
    navigate(from, { replace: true });
  };

  // Handle OTP request
  const handleRequestOtp = async () => {
    setError('');
    setMessage('');
    setLoading(true);

    if (!isValidIndonesianPhone(phone)) {
      setError(t('auth.invalidPhone'));
      setLoading(false);
      return;
    }

    const { error: otpError } = await sendOtp(phone, { shouldCreateUser: false });

    if (otpError) {
      setError(otpError.message || t('auth.otpFailed'));
      setLoading(false);
      return;
    }

    setMessage(t('auth.otpSent'));
    setStep('otp');
    setLoading(false);
  };

  // Handle OTP verification
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { data, error: verifyError } = await verifyOtp(phone, otp);

    if (verifyError) {
      setError(verifyError.message || t('auth.otpInvalid'));
      setLoading(false);
      return;
    }

    setLoading(false);
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src={amperaLogo}
              alt="Ampera"
              className="h-16 mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Link>
          <h1 className="mt-4 text-3xl font-display font-bold text-gray-900">
            {t('auth.welcomeBack')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('auth.loginSubtitle')}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {message}
            </div>
          )}

          {/* Phone Login Form */}
          {step === 'credentials' && (
            <form onSubmit={handlePhoneLogin} className="space-y-4">
              <Field id="phone" label={t('auth.phoneNumber')}>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="08xxxxxxxxxx"
                  className="input-field"
                  required
                />
              </Field>

              <Field id="password" label={t('auth.password')}>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || authLoading}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{t('auth.or')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="secondary"
                onClick={handleRequestOtp}
                disabled={loading}
                className="w-full"
              >
                {t('auth.signInWithOtp')}
              </Button>
            </form>
          )}

          {/* OTP Verification Form */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                {t('auth.otpSentTo', { phone })}
              </p>

              <Field id="otp" label={t('auth.otpCode')}>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  className="input-field text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.verifying') : t('auth.verify')}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setOtp('');
                  setMessage('');
                }}
                className="w-full text-primary-600 text-sm hover:underline"
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          )}

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:underline block"
            >
              {t('auth.forgotPassword')}
            </Link>
            <p className="text-sm text-gray-600">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-primary-600 hover:underline font-medium">
                {t('auth.signUp')}
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            &larr; {t('common.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
